import React, { useRef, useState } from 'react';
import { PageHeader } from '@/components/system/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Upload, FileArchive, CheckCircle2, XCircle, AlertTriangle, Clock, RotateCcw } from 'lucide-react';
import { useSiafoImport, type ImportResult } from '@/hooks/useSiafoImport';
import { cn } from '@/lib/utils';

const ACCEPTED_MIME = 'application/zip,application/x-zip-compressed';

export default function SiafoImportPage({ embedded = false }: { embedded?: boolean }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { status, result, error, progress, statusMessage, jobProgress, runImport, reset } = useSiafoImport();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setSelectedFile(f);
  };

  const handleStart = () => {
    if (selectedFile) runImport(selectedFile);
  };

  const handleReset = () => {
    reset();
    setSelectedFile(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const isProcessing = status === 'uploading' || status === 'processing';

  return (
    <div className={cn('space-y-6', !embedded && 'p-6 max-w-3xl mx-auto')}>
      {/* PageHeader — hidden when embedded as tab in Ferramentas Dev */}
      {!embedded && (
      <PageHeader
        title="Import SIAFO"
        subtitle="Migração histórica — importação única do pacote de dados gerado pelo SIAFO"
      />
      )}

      {/* Upload Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileArchive className="h-5 w-5 text-primary" />
            Data Package SIAFO
          </CardTitle>
          <CardDescription>
            Selecione o arquivo <code className="text-xs bg-muted px-1 py-0.5 rounded">siafo_netuno_export.zip</code> gerado pelo sistema SIAFO.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            className={cn(
              'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
              selectedFile ? 'border-primary/40 bg-primary/[0.03]' : 'border-muted-foreground/20 hover:border-primary/30'
            )}
            onClick={() => !isProcessing && fileRef.current?.click()}
          >
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPTED_MIME}
              className="hidden"
              onChange={handleFileChange}
              disabled={isProcessing}
            />
            {selectedFile ? (
              <div className="flex flex-col items-center gap-2">
                <FileArchive className="h-10 w-10 text-primary" />
                <p className="text-sm font-medium text-foreground">{selectedFile.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="h-10 w-10 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Clique para selecionar o arquivo ZIP</p>
              </div>
            )}
          </div>

          {/* Progress */}
          {isProcessing && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3 animate-spin" />
                  {statusMessage || 'Processando...'}
                </span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
              {jobProgress && jobProgress.processed_records > 0 && (
                <p className="text-xs text-muted-foreground text-right">
                  {jobProgress.processed_records.toLocaleString('pt-BR')} / {jobProgress.total_records.toLocaleString('pt-BR')} registros
                </p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              onClick={handleStart}
              disabled={!selectedFile || isProcessing}
              className="flex-1"
            >
              {isProcessing ? 'Importando...' : 'Iniciar Importação'}
            </Button>
            {(status === 'completed' || status === 'error') && (
              <Button variant="outline" onClick={handleReset}>
                <RotateCcw className="h-4 w-4 mr-1" />
                Nova importação
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {status === 'error' && error && (
        <Card className="border-destructive/30">
          <CardContent className="pt-5">
            <div className="flex items-start gap-3">
              <XCircle className="h-5 w-5 text-destructive mt-0.5" />
              <div>
                <p className="text-sm font-medium text-destructive">Erro na importação</p>
                <p className="text-xs text-muted-foreground mt-1">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Result Report */}
      {result && <ImportReport result={result} />}
    </div>
  );
}

function ImportReport({ result }: { result: ImportResult }) {
  const hasErrors = result.errors.length > 0;
  const totalRecords =
    result.total_inspecoes + result.total_riscos + result.total_evidencias +
    result.total_encerramentos + result.total_hierarquia + result.total_taxonomia;

  const rows = [
    { label: 'Inspeções', count: result.total_inspecoes },
    { label: 'Riscos Classificados', count: result.total_riscos },
    { label: 'Evidências Fotográficas', count: result.total_evidencias },
    { label: 'Encerramentos', count: result.total_encerramentos },
    { label: 'Hierarquia Operacional', count: result.total_hierarquia },
    { label: 'Taxonomia de Risco', count: result.total_taxonomia },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            {hasErrors ? (
              <AlertTriangle className="h-5 w-5 text-warning" />
            ) : (
              <CheckCircle2 className="h-5 w-5 text-success" />
            )}
            Relatório de Importação
          </CardTitle>
          <Badge variant={hasErrors ? 'outline' : 'default'}>
            {hasErrors ? 'Concluída com avisos' : 'Concluída'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-muted/50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-foreground font-display">{totalRecords.toLocaleString('pt-BR')}</p>
            <p className="text-xs text-muted-foreground">Registros importados</p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-destructive font-display">{result.errors.length}</p>
            <p className="text-xs text-muted-foreground">Erros</p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-foreground font-display">{result.batches_processed ?? 0}</p>
            <p className="text-xs text-muted-foreground">Batches processados</p>
          </div>
          <div className="bg-muted/50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-foreground font-display">{(result.duration_ms / 1000).toFixed(1)}s</p>
            <p className="text-xs text-muted-foreground">Tempo de execução</p>
          </div>
        </div>

        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={r.label} className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0">
              <span className="text-sm text-muted-foreground">{r.label}</span>
              <span className="text-sm font-medium text-foreground">{r.count.toLocaleString('pt-BR')}</span>
            </div>
          ))}
        </div>

        {hasErrors && (
          <div className="border border-destructive/20 bg-destructive/5 rounded-lg p-3 space-y-1">
            <p className="text-xs font-medium text-destructive">Erros encontrados:</p>
            <div className="max-h-40 overflow-y-auto space-y-0.5">
              {result.errors.map((e, i) => (
                <p key={i} className="text-xs text-destructive/80 font-mono">{e}</p>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
